import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, Skeleton, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { isApiError } from '@/lib/apiError';
import { Rol } from '@/types/enums';

import { DatosGeneralesSection } from '../components/DatosGeneralesSection';
import { EliminarProtocoloDialog } from '../components/EliminarProtocoloDialog';
import { ProtocoloDetailHeader } from '../components/ProtocoloDetailHeader';
import { SeccionAnalisis } from '../components/SeccionAnalisis';
import { SeccionEnvios } from '../components/SeccionEnvios';
import { SeccionInformes } from '../components/SeccionInformes';
import { SeccionInterpretacion } from '../components/SeccionInterpretacion';
import { useProtocolo } from '../hooks/useProtocolos';

export function ProtocoloDetailPage(): JSX.Element {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { data: protocolo, isLoading, isError, error } = useProtocolo(uid);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const canManage =
    currentUser?.rol === Rol.ADMINISTRADOR ||
    currentUser?.rol === Rol.TECNICO_LABORATORIO;

  if (isLoading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={220} height={36} />
          <Skeleton variant="rounded" width={70} height={24} />
        </Stack>
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  if (isError || !protocolo) {
    const isNotFound = isApiError(error) && error.isNotFound();
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {isNotFound
            ? 'Protocolo no encontrado.'
            : error instanceof Error
              ? error.message
              : 'No se pudo cargar el protocolo.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/protocolos')}
        >
          Volver al listado
        </Button>
      </Box>
    );
  }

  return (
    <>
      <ProtocoloDetailHeader
        protocolo={protocolo}
        canManage={canManage}
        onEliminar={() => setDeleteDialogOpen(true)}
      />

      <DatosGeneralesSection
        protocolo={protocolo}
        canEdit={canManage && !protocolo.informe_final_emitido}
      />

      <SeccionAnalisis
        uidProtocolo={protocolo.uid_protocolo}
        canManage={canManage}
        isClosed={protocolo.informe_final_emitido}
        numeroProtocolo={protocolo.numero_protocolo}
        uidEstablecimiento={protocolo.uid_establecimiento}
      />

      <SeccionInterpretacion
        protocolo={protocolo}
        puedeEditar={canManage && !protocolo.informe_final_emitido}
      />

      <SeccionInformes
        uidProtocolo={protocolo.uid_protocolo}
        isClosed={protocolo.informe_final_emitido}
        canManage={canManage}
      />

      <SeccionEnvios
        uidProtocolo={protocolo.uid_protocolo}
        protocolo={protocolo}
        canManage={canManage}
      />

      <EliminarProtocoloDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        protocolo={protocolo}
        onEliminado={() => navigate('/protocolos')}
      />
    </>
  );
}
